import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
    AbstractControl,
    UntypedFormArray,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import {
    EMPTY_ARRAY_LENGTH,
    ISpreadSheet,
    ModalComponent,
    ModalData,
    ModalTitleIcons,
    NumberUtils,
    getSheetsInSparsArraysFromFile,
} from '@modeso/aumico-lib-core-fe';
import { FixedAssetsSchedule, IAccount, IAccountMappingTemplate } from '@modeso/types__aumico-lib-annualreport-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import fastDeepEqual from 'fast-deep-equal';
import { cloneDeep, differenceBy } from 'lodash-es';
import { Observable, distinctUntilChanged, first, of } from 'rxjs';
import { FixedAssetsScheduleFileService } from '../../component-services/fixed-assets-schedule/fixed-assets-schedule-file.service';
import { FixedAssetsScheduleValidationService } from '../../component-services/fixed-assets-schedule/fixed-assets-schedule-validation.service';
import { FixedAssetsScheduleService } from '../../component-services/fixed-assets-schedule/fixed-assets-schedule.service';
import { INITIAL_FAS_REFERENCE_SELECTION_CAPTION } from '../../constants/fas-constants';
import { IFasAccountValue } from '../../interfaces/fas-account-value.interface';
import { FixedAssetsScheduleFormData } from '../../interfaces/fixed-assets-schedule-form-data.interface';
import { FixedAssetsScheduleFormMovement } from '../../interfaces/fixed-assets-schedule-form-movement.interface';
import { IFixedAssetsScheduleReference } from '../../interfaces/fixed-assets-schedule-reference.interface';
import { AccountMapper } from '../../mappers/account.mapper';
import { FixedAssetsScheduleMapper } from '../../mappers/fixed-assets-schedule-mapper';

@UntilDestroy()
@Component({
    selector: 'fixed-assets-schedule-form',
    templateUrl: 'fixed-assets-schedule-form.component.html',
    styleUrls: ['fixed-assets-schedule-form.component.scss'],
    providers: [FixedAssetsScheduleService, FixedAssetsScheduleValidationService, FixedAssetsScheduleFileService],
})
export class FixedAssetsScheduleForm implements OnInit {
    private language: string;
    @Input() set currentLanguage(value: string) {
        this.language = value;

        if (value != null && this.mappedTemplate != null) {
            this.handleMappingData();
        }
    }

    private mappedTemplate: IAccountMappingTemplate | undefined;
    @Input() set mappingData(value: IAccountMappingTemplate) {
        this.mappedTemplate = cloneDeep<IAccountMappingTemplate>(value);

        if (this.language == null) {
            return;
        }

        this.handleMappingData();
    }

    private fixedAssetsScheduleData: FixedAssetsSchedule | null = null;
    @Input() set fixedAssetsScheduleSavedData(value: FixedAssetsSchedule | null) {
        this.fixedAssetsScheduleData = cloneDeep<FixedAssetsSchedule | null>(value);

        if (this.mappedTemplate == null || value == null) {
            // can't use saved data without mappingData
            return;
        }

        this.handleFixedAssetsScheduleSavedData();
    }

    private _fixedAssetsSchedulePreviousYear: FixedAssetsSchedule | undefined;
    @Input() set fixedAssetsSchedulePreviousYear(value: FixedAssetsSchedule | undefined) {
        this._fixedAssetsSchedulePreviousYear = cloneDeep<FixedAssetsSchedule | undefined>(value);
        if (this.mappedTemplate == null || value == null) {
            return;
        }
        
        // Don't autofill if we already have saved data
        if (this.fixedAssetsScheduleData != null) {
            return;
        }
        
        this.handleFixedAssetsScheduleAutofillData();
    }

    @Input() balanceOpeningDate: Date;
    @Input() balanceClosingDate: Date;
    @Input() noPreviousData = false;
    @Input() periodStartDate: Date | undefined;

    @Input() reportLocale: string;

    referenceOptions: IFixedAssetsScheduleReference[] = [];

    isReferenceOptionSelected = false;

    fixedAssetsScheduleFormToBeUsed: FixedAssetsScheduleFormData | undefined;

    fixedAssetsScheduleMovements: FixedAssetsScheduleFormMovement[] | undefined;

    movements: UntypedFormArray | undefined;
    form: UntypedFormGroup = this.formBuilder.group({
        reference: new UntypedFormControl(null),
    });

    uploadedFasAccountsValues: IFasAccountValue[] = [];

    private formInitialized = false;
    protected colTitlesTranslation1 = [
        'aumico_annual_report_fas_costs',
        'aumico_annual_report_fas_additions_column',
        'aumico_annual_report_fas_disposals_column',
        'aumico_annual_report_fas_transfers_column',
        'aumico_annual_report_fas_costs',
    ];
    protected colTitlesTranslation2 = [
        'aumico_annual_report_fas_depreciations',
        'aumico_annual_report_fas_depr_additions_column',
        'aumico_annual_report_fas_depr_disposals_column',
        'aumico_annual_report_fas_depr_transfers_column',
        'aumico_annual_report_fas_depr_cumulated',
    ];
    protected colTitlesTranslation3 = [
        'aumico_annual_report_fas_valueAppreciation',
        'aumico_annual_report_fas_bookvalue_current',
        'aumico_annual_report_fas_bookvalue_previous',
    ];

    selectedReferenceOption: IFixedAssetsScheduleReference | null = null;

    depreciationsTableExist = true;
    valueAppreciationTableExist = true;

    @Output() fixedAssetsScheduleOutput = new EventEmitter<FixedAssetsSchedule>();

    constructor(
        private readonly fixedAssetsScheduleService: FixedAssetsScheduleService,
        private readonly fixedAssetsScheduleValidationService: FixedAssetsScheduleValidationService,
        private readonly fixedAssetsScheduleFileService: FixedAssetsScheduleFileService,
        private formBuilder: UntypedFormBuilder,
        private readonly accountMapper: AccountMapper,
        public readonly dialog: MatDialog,
    ) {}

    ngOnInit(): void {}

    private handleMappingData(): void {
        this.referenceOptions = this.fixedAssetsScheduleService.getReferenceOptions(this.mappedTemplate);

        if (this.fixedAssetsScheduleData != null) {
            this.handleFixedAssetsScheduleSavedData();
            return;
        }

        if (this._fixedAssetsSchedulePreviousYear != null) {
            this.handleFixedAssetsScheduleAutofillData();
            return;
        }

        const initialReferenceOption = this.referenceOptions.find(
            (option) => option.referenceCaption?.trim() === INITIAL_FAS_REFERENCE_SELECTION_CAPTION,
        );

        if (initialReferenceOption == null) {
            return;
        }

        this.selectedReferenceOption = initialReferenceOption;

        this.fixedAssetsScheduleFormToBeUsed = this.fixedAssetsScheduleService.getInitialFixedAssetsScheduleFormData(
            this.mappedTemplate,
            initialReferenceOption,
            this.reportLocale,
        );

        this.fixedAssetsScheduleMovements = this.fixedAssetsScheduleFormToBeUsed.movements;

        if (this.formInitialized) {
            this.updateForm();
        } else {
            this.initializeForm();
            this.handleFormChange();
        }

        this.isReferenceOptionSelected = true;
    }

    private handleFixedAssetsScheduleSavedData(): void {
        const isFixedAssetsScheduleDataValid =
            this.fixedAssetsScheduleValidationService.isFixedAssetsScheduleDataValidateAgainstMapping(
                this.fixedAssetsScheduleData,
                this.mappedTemplate,
            );
        if (!isFixedAssetsScheduleDataValid) {
            return;
        }

        this.selectedReferenceOption = this.referenceOptions.find(
            (option) =>
                option.reference === this.fixedAssetsScheduleData.reference &&
                option.referenceType === this.fixedAssetsScheduleData.referenceType,
        );

        this.fixedAssetsScheduleFormToBeUsed = this.fixedAssetsScheduleService.getSavedFixedAssetsScheduleFormData(
            this.mappedTemplate,
            this.selectedReferenceOption,
            this.fixedAssetsScheduleData,
        );

        this.fixedAssetsScheduleMovements = this.fixedAssetsScheduleFormToBeUsed.movements;
        this.isReferenceOptionSelected = true;

        if (this.formInitialized) {
            this.updateForm();
        } else {
            this.initializeForm();
            this.handleFormChange();
        }
    }

    private handleFixedAssetsScheduleAutofillData(): void {
        this.selectedReferenceOption = this.referenceOptions.find(
            (option) =>
                option.reference === this._fixedAssetsSchedulePreviousYear.reference &&
                option.referenceType === this._fixedAssetsSchedulePreviousYear.referenceType,
        );

        this.fixedAssetsScheduleFormToBeUsed =
            this.fixedAssetsScheduleService.getAutofilledFixedAssetsScheduleFormDataFromPreviousYear(
                this.mappedTemplate,
                this.selectedReferenceOption,
                this._fixedAssetsSchedulePreviousYear,
            );

        this.fixedAssetsScheduleMovements = this.fixedAssetsScheduleFormToBeUsed.movements;
        this.isReferenceOptionSelected = true;

        if (this.formInitialized) {
            this.updateForm();
        } else {
            this.initializeForm();
            this.handleFormChange();
        }

        this.isReferenceOptionSelected = true;
    }

    onSelectedReferenceChange(selectedReference: IFixedAssetsScheduleReference | null): void {
        this.selectedReferenceOption = selectedReference;

        if (selectedReference == null) {
            this.isReferenceOptionSelected = false;
            this.fixedAssetsScheduleFormToBeUsed = undefined;
            this.fixedAssetsScheduleMovements = [];
            return;
        }

        if (
            this._fixedAssetsSchedulePreviousYear != null &&
            this.fixedAssetsScheduleService.canApplyAutofill(
                selectedReference,
                this._fixedAssetsSchedulePreviousYear.reference,
                this._fixedAssetsSchedulePreviousYear.referenceType,
            )
        ) {
            this.fixedAssetsScheduleFormToBeUsed =
                this.fixedAssetsScheduleService.getAutofilledFixedAssetsScheduleFormDataFromPreviousYear(
                    this.mappedTemplate,
                    selectedReference,
                    this._fixedAssetsSchedulePreviousYear,
                );
        } else {
            this.fixedAssetsScheduleFormToBeUsed =
                this.fixedAssetsScheduleService.getInitialFixedAssetsScheduleFormData(
                    this.mappedTemplate,
                    selectedReference,
                    this.reportLocale,
                );
        }

        this.fixedAssetsScheduleMovements = this.fixedAssetsScheduleFormToBeUsed.movements;

        // check if file is uploaded
        if (this.uploadedFasAccountsValues.length !== EMPTY_ARRAY_LENGTH) {
            this.fixedAssetsScheduleMovements = this.fixedAssetsScheduleService.fillMovementsFromUploadedFile(
                this.fixedAssetsScheduleMovements,
                this.selectedReferenceOption,
                this.mappedTemplate,
                this.uploadedFasAccountsValues,
            );
        }

        if (this.formInitialized) {
            this.updateForm();
        } else {
            this.initializeForm();
            this.handleFormChange();
        }

        this.isReferenceOptionSelected = true;
    }

    private initializeForm(): void {
        this.formInitialized = true;
        this.form = this.formBuilder.group({
            reference: new UntypedFormControl(this.fixedAssetsScheduleFormToBeUsed.reference),
            costsLabel: new UntypedFormControl(this.fixedAssetsScheduleFormToBeUsed.costsLabel),
            depreciationLabel: new UntypedFormControl(this.fixedAssetsScheduleFormToBeUsed.depreciationLabel),
            valueAppreciationLabel: new UntypedFormControl(this.fixedAssetsScheduleFormToBeUsed.valueAppreciationLabel),
            movements: this.formBuilder.array([]),
        });
        this.movements = this.getAllAsFormArray(this.fixedAssetsScheduleMovements);
        this.form.setControl('movements', this.movements);

        // listen to form change
        this.listenToFormChange();
    }

    private updateForm(): void {
        this.form.get('reference').setValue(this.fixedAssetsScheduleFormToBeUsed.reference);
        this.form.get('costsLabel').setValue(this.fixedAssetsScheduleFormToBeUsed.costsLabel);
        this.form.get('depreciationLabel').setValue(this.fixedAssetsScheduleFormToBeUsed.depreciationLabel);
        this.form.get('valueAppreciationLabel').setValue(this.fixedAssetsScheduleFormToBeUsed.valueAppreciationLabel);
        this.movements = this.getAllAsFormArray(this.fixedAssetsScheduleMovements);
        this.form.setControl('movements', this.movements);
    }

    private getAllAsFormArray(tableData: FixedAssetsScheduleFormMovement[]): UntypedFormArray {
        const formGroups: AbstractControl[] = tableData.map((entry) => this.addFormGroup(entry));
        return new UntypedFormArray(formGroups);
    }
    private addFormGroup(movement: FixedAssetsScheduleFormMovement): UntypedFormGroup {
        const formGroup = new UntypedFormGroup({
            name: new UntypedFormControl(movement.name),
            caption: new UntypedFormControl(movement.caption),
            openingValue: new UntypedFormControl(movement.openingValue),
            closingValue: new UntypedFormControl(movement.closingValue),
        });
        const costsGroup = new UntypedFormGroup({
            openingValue: new UntypedFormControl({ value: movement.costs.openingValue, disabled: false }),
            additions: new UntypedFormControl(movement.costs.additions),
            disposals: new UntypedFormControl(movement.costs.disposals),
            transfers: new UntypedFormControl(movement.costs.transfers),
            otherValue: new UntypedFormControl(movement.costs.otherValue),
            closingValue: new UntypedFormControl({ value: movement.costs.closingValue, disabled: false }),
            isValid: new UntypedFormControl(movement.costs.isValid),
        });

        formGroup.setControl('costs', costsGroup);

        if (this.depreciationsTableExist) {
            const depreciationsGroup = new UntypedFormGroup({
                openingValue: new UntypedFormControl(movement.depreciations.openingValue),
                additions: new UntypedFormControl(movement.depreciations.additions),
                disposals: new UntypedFormControl(movement.depreciations.disposals),
                transfers: new UntypedFormControl(movement.depreciations.transfers),
                otherValue: new UntypedFormControl(movement.depreciations.otherValue),
                closingValue: new UntypedFormControl({ value: movement.depreciations.closingValue, disabled: false }),
                isValid: new UntypedFormControl(movement.depreciations.isValid),
            });
            formGroup.setControl('depreciations', depreciationsGroup);
        }

        if (this.valueAppreciationTableExist) {
            const valueAppreciationsGroup = new UntypedFormGroup({
                openingValue: new UntypedFormControl(movement.valueAppreciations.openingValue),
                otherValue: new UntypedFormControl(movement.valueAppreciations.otherValue),
                isValid: new UntypedFormControl(movement.valueAppreciations.isValid),
            });

            formGroup.setControl('valueAppreciations', valueAppreciationsGroup);
        }

        return formGroup;
    }

    private listenToFormChange(): void {
        this.form.valueChanges
            .pipe(
                distinctUntilChanged((prev, curr) => fastDeepEqual(prev, curr)),
                untilDestroyed(this),
            )
            .subscribe(() => {
                this.handleFormChange();
            });
    }

    private handleFormChange(): void {
        this.calculate('valueAppreciations');
        this.calculate('costs');
        this.calculate('depreciations');
        this.validateRows();
        this.emitFixedAssetsScheduleOutput();
    }

    private emitFixedAssetsScheduleOutput(): void {
        const formValue: FixedAssetsScheduleFormData = cloneDeep<FixedAssetsScheduleFormData>(this.form.value);
        const fixedAssetsScheduleOutput = FixedAssetsScheduleMapper.mapFasFormDataToOutputData(formValue);
        this.fixedAssetsScheduleOutput.emit(fixedAssetsScheduleOutput);
    }

    hasChildren(movements: FixedAssetsScheduleFormMovement[], index: number): boolean {
        const currentLevel = movements[index]?.level ?? 0;
        if (movements.length > index + 1) {
            //There are more rows
            if (movements[index + 1].level > currentLevel) {
                return true;
            }
        }
        return false;
    }

    private calculate(section: string): void {
        const formMovements: FixedAssetsScheduleFormMovement[] = this.form.value.movements;
        const fixedAssetsScheduleMovements = this.fixedAssetsScheduleMovements;
        // Traverse the array backwards
        for (let index = formMovements.length - 1; index >= 0; index--) {
            const movement = formMovements[index];
            //fasMovement is only required because the level is not saved on the movement...
            const fasMovement = fixedAssetsScheduleMovements[index];

            let openingValue, additions, disposals, transfers;

            if (this.hasChildren(this.fixedAssetsScheduleMovements, index)) {
                // Parent node: Aggregate values from children
                openingValue = this.sumChildValues(formMovements, index, fasMovement.level, section, 'openingValue');

                if (section !== 'valueAppreciations') {
                    additions = this.sumChildValues(formMovements, index, fasMovement.level, section, 'additions');
                    disposals = this.sumChildValues(formMovements, index, fasMovement.level, section, 'disposals');
                    transfers = this.sumChildValues(formMovements, index, fasMovement.level, section, 'transfers');
                }
            } else {
                // Child node: Calculate normally
                openingValue = movement[section]?.openingValue ?? 0;
                if (section !== 'valueAppreciations') {
                    additions = movement[section]?.additions ?? 0;
                    disposals = movement[section]?.disposals ?? 0;
                    transfers = movement[section]?.transfers ?? 0;
                }
            }

            // Set opening value
            formMovements[index][section].openingValue = openingValue;
            this.movements.at(index).get(section).get('openingValue').setValue(openingValue, { emitEvent: false });

            if (section !== 'valueAppreciations') {
                // Calculate and set the closing value
                const closingValue = NumberUtils.sumIgnoringInfiniteNumbers(
                    [openingValue, additions, -1 * disposals, transfers],
                    0,
                );

                formMovements[index][section].additions = additions;
                formMovements[index][section].disposals = disposals;
                formMovements[index][section].transfers = transfers;
                formMovements[index][section].closingValue = closingValue;

                this.movements.at(index).get(section).get('additions').setValue(additions, { emitEvent: false });
                this.movements.at(index).get(section).get('disposals').setValue(disposals, { emitEvent: false });
                this.movements.at(index).get(section).get('transfers').setValue(transfers, { emitEvent: false });
                this.movements.at(index).get(section).get('closingValue').setValue(closingValue, { emitEvent: false });
            }
        }
    }

    private sumChildValues(
        movements: FixedAssetsScheduleFormMovement[],
        parentIndex: number,
        parentLevel: number,
        sectionName: string,
        fieldName: string,
    ): number {
        let sum = 0;

        for (let index = parentIndex + 1; index < movements.length; index++) {
            const movement = movements[index];
            //fasMovement is only required because the level is not saved on the movement...
            const fasMovement = this.fixedAssetsScheduleMovements[index];

            // Check if the movement is a direct child of the parent
            if (fasMovement.level === parentLevel + 1) {
                sum += movement[sectionName][fieldName] ?? 0;
            } else if (fasMovement.level > parentLevel + 1) {
                //Decide if it needs to be counted
                if (index === parentIndex + 1) {
                    sum += movement[sectionName][fieldName] ?? 0;
                }
            } else if (fasMovement.level <= parentLevel) {
                // Exit the loop if the level is less than or equal to parent level,
                // indicating we've moved past the children of the parent
                break;
            }
        }
        movements[parentIndex][sectionName][fieldName] = sum;
        return sum;
    }

    private validateRows(): void {
        this.form.value.movements.forEach((movement, index) => {
            const costs = movement.costs != null ? movement.costs.closingValue : 0;
            const depreciations = movement.depreciations != null ? movement.depreciations.closingValue : 0;
            const valueAppreciations =
                movement.valueAppreciations != null ? movement.valueAppreciations.openingValue : 0;

            const costsAndDepreciationsDifference = NumberUtils.subtractNumbers(costs, depreciations);

            const sumToCompare = NumberUtils.sumIgnoringInfiniteNumbers(
                [costsAndDepreciationsDifference, valueAppreciations],
                0,
            );
            this.movements
                .at(index)
                .get('costs')
                .get('isValid')
                .setValue(sumToCompare === movement.closingValue, {
                    emitEvent: false,
                });
            this.movements
                .at(index)
                .get('depreciations')
                .get('isValid')
                .setValue(sumToCompare === movement.closingValue, {
                    emitEvent: false,
                });
            this.movements
                .at(index)
                .get('valueAppreciations')
                .get('isValid')
                .setValue(sumToCompare === movement.closingValue, {
                    emitEvent: false,
                });
        });
    }

    public compareSelections(object1: IFixedAssetsScheduleReference, object2: IFixedAssetsScheduleReference): boolean {
        return object1.reference === object2.reference;
    }

    onFasFileUpload(file: File): void {
        let parsedFileObservable: Observable<ISpreadSheet>;
        if (file != null) {
            parsedFileObservable = getSheetsInSparsArraysFromFile(file);
        } else {
            parsedFileObservable = of(null);
        }
        parsedFileObservable.pipe(first(), untilDestroyed(this)).subscribe((parsedFile) => {
            if (parsedFile == null) {
                this.uploadedFasAccountsValues = [];
                return;
            }

            const isUploadedFileValid = this.fixedAssetsScheduleFileService.isUploadedFileValid(parsedFile);

            if (!isUploadedFileValid) {
                this.showInvalidFileFormatWariningDialog();
                return;
            }

            this.uploadedFasAccountsValues = this.fixedAssetsScheduleFileService.mapParsedFileToFasAccounts(parsedFile);

            this.fixedAssetsScheduleMovements = this.fixedAssetsScheduleService.fillMovementsFromUploadedFile(
                this.fixedAssetsScheduleMovements,
                this.selectedReferenceOption,
                this.mappedTemplate,
                this.uploadedFasAccountsValues,
            );

            this.showWariningDialogForUnmappedAccounts();

            this.updateForm();
        });
    }

    private showInvalidFileFormatWariningDialog(): void {
        this.dialog.open<ModalComponent, ModalData>(ModalComponent, {
            disableClose: false,
            panelClass: 'custom-modalbox',
            data: {
                showCloseButton: true,
                titleIcon: ModalTitleIcons.WARNING,
                content: {
                    text: ['aumico_annual_report_fas_invalid_file_format_warning_text'],
                    icon: 'aumico-pattern-icon-alert-triangle',
                },
            },
        });
    }

    private showWariningDialogForUnmappedAccounts(): void {
        const mappedAccounts = this.accountMapper.getAccountsOfTemplate(this.mappedTemplate);

        const unmappedAccounts = differenceBy<IFasAccountValue, IAccount>(
            this.uploadedFasAccountsValues,
            mappedAccounts,
            'accountNumber',
        );

        if (unmappedAccounts.length === EMPTY_ARRAY_LENGTH) {
            return;
        }

        const validUnmappedAccounts = unmappedAccounts.filter(
            (account) => account.accountNumber != null && account.accountName != null,
        );

        if (validUnmappedAccounts.length === EMPTY_ARRAY_LENGTH) {
            return;
        }

        let unmappedAccountsText = '';
        for (const unmappedAccount of validUnmappedAccounts) {
            unmappedAccountsText =
                unmappedAccountsText + `${unmappedAccount.accountNumber} ${unmappedAccount.accountName} <br>`;
        }

        this.dialog.open<ModalComponent, ModalData>(ModalComponent, {
            disableClose: false,
            panelClass: 'custom-modalbox',
            data: {
                showCloseButton: true,
                titleIcon: ModalTitleIcons.WARNING,
                content: {
                    text: ['aumico_annual_report_fas_unmapped_uploaded_accounts_warning'],
                    descriptiveText: unmappedAccountsText,
                    icon: 'document-information',
                },
            },
        });
    }
}
